import CollapseFilter from "components/Common/CollapseFilter";
import DatePickerCustom from "components/Common/DatePickerCustom";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import { useRole } from "components/Hooks/UserHooks";
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { APP_TYPE_OPTIONS, ARR_INDEX_TEXT_DAY_OF_WEEK } from "helpers/constans";
import { formatNumberWithCommas } from "helpers/format";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { getPushStatistics as onGetStatistics } from "../../../store/thunks";
import ChartLine from "./ChartLine";
import DropdownOption from "components/Common/DropdownOption";
import { Option } from "api/types/_public";
import DropdownOptionApp from "components/Common/DropdownOptionApp";


const SORT_BY_DEFAULT = 'date';
const ORDER_BY_DEFAULT = 'DESC';


registerLocale('en', en);
registerLocale('ko', ko);

const Statistic = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();
    const navigate = useNavigate();

    const APP_TYPE_OPTIONS_LANG = APP_TYPE_OPTIONS?.map(
        (item: any) => ({ value: item?.value, label: t(item?.label) })
    );

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 100),
        sort_by: withDefault(StringParam, SORT_BY_DEFAULT),
        order_by: withDefault(StringParam, ORDER_BY_DEFAULT),
        start_date: withDefault(StringParam, `${moment(new Date(new Date().setDate(new Date().getDate() - 30))).format("Y-MM-DD")}`),
        end_date: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        app_type: withDefault(StringParam, `${APP_TYPE_OPTIONS_LANG[0]?.value}`),
    });

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Statistic;

    const ManagementProperties = createSelector(
        selectLayoutState,
        (state) => ({
            statistics: state.pushStatistics,
            isStatisticSuccess: state.isPushStatisticSuccess,
            isStatisticLoading: state.isPushStatisticLoading,
            error: state.error,
        })
    );

    const { statistics, isStatisticLoading } = useSelector(ManagementProperties);
    const [isCallAllOptionLoading, setIsCallAllOptionLoading] = useState<boolean>(true);

    const [dateSearch, setDateSearch] = useState<any[]>([query?.start_date ? moment(query?.start_date, 'Y-MM-DD').toDate() : null, query?.end_date ? moment(query?.end_date || '', 'Y-MM-DD').toDate() : null]);
    const [startDate, endDate] = dateSearch;

    const [appTypeSearch, setAppTypeSearch] = useState<Option | null>(APP_TYPE_OPTIONS_LANG?.find((item) => String(item?.value) === String(query?.app_type)) || null);


    useEffect(() => {
        dispatch(onGetStatistics(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {
        setQuery({
            ...query,
            page: 1,
            start_date: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            end_date: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            app_type: appTypeSearch?.value || '',
            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            ...query,
            start_date: `${moment().subtract(30, 'days').format("Y-MM-DD")}`,
            end_date: `${moment().format("Y-MM-DD")}`,
            app_type: `${APP_TYPE_OPTIONS_LANG[0]?.value}`,
            sort_by: SORT_BY_DEFAULT,
            order_by: ORDER_BY_DEFAULT,
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push");
        setDateSearch([moment(queryNew?.start_date || "", 'Y-MM-DD').toDate(), moment(queryNew?.end_date || "", 'Y-MM-DD').toDate()]);
        setAppTypeSearch(APP_TYPE_OPTIONS_LANG[0]);
    };

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Date'),
                accessor: "date",
                filterable: true,
                sortable: true,
                thWidth: 150,
                thClass: 'align-middle text-start',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const arrDate = String(cell?.value || '').split(' ');
                    const num = moment(cell?.value).days(); // 0 is Sunday , 1 is Monday, ... , 6 is Saturday
                    const classText = num === 0 ? 'text-danger row-bg-danger' : (num === 6 ? 'text-primary row-bg-primary' : '');
                    const textNote = t(`STANDS_${ARR_INDEX_TEXT_DAY_OF_WEEK[num]}`);
                    return (
                        !!item?.text_row ? <strong>{item?.text_row} </strong> :
                            <div className="text-start" style={{ minWidth: '120px' }}>
                                <span className={classText}>{moment(arrDate?.[0] || '', 'Y-MM-DD').format("MM-DD")} ({textNote})</span>
                            </div>

                    )
                },
            },
            {
                Header: t('Total Sent'),
                accessor: "total_sent",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 180,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '120px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Number of sending'),
                accessor: "sent",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 180,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '120px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Number of clicks'),
                accessor: "clicked",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 180,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '120px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Number of ctr rate'),
                accessor: "ctr",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 180,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '120px' }}>{formatNumberWithCommas(cell?.value)} %</div>
                        </>
                    )
                },
            },
            {
                Header: t('Number of Success'),
                accessor: "success",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 180,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '120px' }}>{formatNumberWithCommas(item?.sent)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Success Rate'),
                accessor: "success_rate",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 180,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '120px' }}>{formatNumberWithCommas(cell?.value)} %</div>
                        </>
                    )
                },
            },
            {
                Header: t('Number of Failed'),
                accessor: "failed",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 180,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '120px' }}>{formatNumberWithCommas(cell?.value)}</div>
                        </>
                    )
                },
            },
            {
                Header: t('Failure Rate'),
                accessor: "failure_rate",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 180,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-end pe-3 " style={{ minWidth: '120px' }}>{formatNumberWithCommas(cell?.value)} %</div>
                        </>
                    )
                },
            },
        ],
        [i18n?.language, userPermissions, query?.media_id]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy });
        });
    }, []);

    const handleChangePicker = (values: any[] = []) => {
        setDateSearch((_prev: any) => values);
    }

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {

                setIsCallAllOptionLoading((_prev) => false);
            } catch (error: any) {
                setIsCallAllOptionLoading((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, []);

    useEffect(() => {

        document.title = `${t("Push Statistics")} - ${t("Home")} | ShowStar`;
        document.body.classList.remove("vertical-sidebar-enable");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    const categories = useMemo(() => {
        return statistics?.items?.map((item: any) => item?.date).reverse() || [];
    }, [statistics?.items, i18n?.language]);

    const series = useMemo(() => {
        const obChart = statistics?.items?.reduce((ob: any, item: any) => {
            ob.sending?.push(Number(String(item?.sent).replace(/,/g, '')));
            ob.clicks?.push(Number(String(item?.clicked).replace(/,/g, '')));
            ob.ctr?.push(Number(String(item?.ctr).replace(/,/g, '')));
            return ob;
        }, { sending: [], clicks: [], ctr: [] }) || [];
        return (
            [
                {
                    name: t('Number of sending'),
                    type: 'column',
                    data: obChart?.sending?.reverse(),
                },
                {
                    name: t('Number of clicks'),
                    type: 'line',
                    data: obChart?.clicks?.reverse(),
                },
                {
                    name: t('Number of ctr rate'),
                    type: 'line',
                    data: obChart?.ctr?.reverse(),
                },
            ]
        )
    }, [statistics?.items, i18n?.language]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('Push Statistics')} pageTitle={t('Home')} />
                    <Row>
                        <Col sm={12} md={12}>
                            <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }} className="ribbon-box right">
                                <div className="ribbon round-shape" style={{ background: 'rgb(40 99 213)', fontWeight: 300, fontSize: '0.8rem', top: '11px' }}>{t('This data is analyzed per 10 mins')}</div>
                                <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                                    <Row className="g-4 align-items-center">
                                        <div className="col-sm">
                                            <div>
                                                <h5 className="card-title mb-0">
                                                    <span className="me-2">{t('Total')}:</span>
                                                    <CountUp
                                                        start={0}
                                                        end={statistics?.items?.length || 0}
                                                        duration={1}
                                                        className="text-primary"
                                                    />
                                                </h5>
                                            </div>
                                        </div>
                                    </Row>
                                </CardHeader>
                                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                                    <CollapseFilter>
                                        <Row className="g-4 align-items-center mt-0">
                                            <Col sm={6} md={3} xl={3} xxl={3} className='mt-3 mt-md-2'>
                                                <LabelWrapper label={t('App')} isShow={true}>
                                                    <DropdownOptionApp
                                                        name=""
                                                        dataList={APP_TYPE_OPTIONS_LANG}
                                                        placeholder={`${t("App")}...`}
                                                        className="search-filter-category-type dropdown-status-rounded"
                                                        classNamePrefix="name-prefix"
                                                        initialValue={appTypeSearch || null}
                                                        onChangeSelect={(e: any) => setAppTypeSearch(e)}
                                                        isHasOptionAll={true}
                                                        optionAll={{ label: t('All'), value: '' }}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col
                                                sm={6}
                                                md={6}
                                                xl={3}
                                                xxl={3}
                                                className="date-picker-wrapper-custom mt-3 mt-md-2"
                                            >
                                                <LabelWrapper
                                                    label={t("Date")}
                                                    isShow={!!startDate || !!endDate}
                                                >
                                                    <DatePickerCustom
                                                        placeholder={`${t("Start Date")} - ${t("End Date")}`}
                                                        startDate={startDate || null}
                                                        endDate={endDate || null}
                                                        onChangePicker={handleChangePicker}
                                                        showOptions={[
                                                            "today",
                                                            "yesterday",
                                                            "two_day_ago",
                                                            "last_3_days",
                                                            "last_7_days",
                                                            "last_14_days",
                                                            "last_30_days",
                                                            "this_week",
                                                            "last_week",
                                                            "this_month",
                                                            "last_month",
                                                        ]}
                                                        maxDate={new Date(new Date().setDate(new Date().getDate()))}
                                                    />
                                                </LabelWrapper>
                                            </Col>
                                            <Col sm={12} md={6} xl={6} xxl={6} className="hstack gap-1   justify-content-center justify-content-sm-between mt-3 mt-md-2">
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary me-2 "
                                                        onClick={searchData}
                                                        disabled={
                                                            isStatisticLoading || isCallAllOptionLoading
                                                        }
                                                    >
                                                        {isStatisticLoading || isCallAllOptionLoading ? (
                                                            <div
                                                                className="me-1"
                                                                style={{
                                                                    display: "inline-table",
                                                                    verticalAlign: "top",
                                                                }}
                                                            >
                                                                <Spinner
                                                                    size="sm"
                                                                    style={{ width: "14px", height: "14px" }}
                                                                ></Spinner>
                                                            </div>
                                                        ) : (
                                                            <i className="ri-search-line align-bottom me-1"></i>
                                                        )}{" "}
                                                        {t("Button Search")}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary fs-14"
                                                        onClick={resetData}
                                                    >
                                                        <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                        {t("Button Reset")}
                                                    </button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CollapseFilter>
                                </div>
                                <ChartLine
                                    className="my-3"
                                    titles={[t('Number of sending'), t('Number of clicks'), t('Number of ctr rate')]}
                                    categories={categories}
                                    series={series}
                                />
                                <CardBody className="pt-3">
                                    <TableContainer
                                        className="custom-header-css"
                                        divClass="table-responsive table-card"
                                        tableClass="align-middle table-bordered-dashed table-have-first-row-is-total sticky-table-daily-statistics"
                                        theadClass="table-light text-muted"
                                        columns={columns}
                                        data={statistics?.items?.length ? (
                                            [
                                                {
                                                    ...statistics?.total_data,
                                                    text_row: t('Sum')
                                                },
                                                ...statistics?.items]
                                        ) : []}
                                        customPageSize={query.limit}
                                        customPageIndex={query.page - 1}
                                        totalRecords={statistics?.pagination?.total}
                                        customPageCount={Math.ceil(Number(statistics?.pagination?.total) / Number(statistics?.pagination?.limit))}
                                        handleChangePage={handleChangePage}
                                        manualSorting={true}
                                        sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                        handleChangeSorting={handleChangeSorting}
                                        isLoading={isStatisticLoading}
                                        isShowPagination={false}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Statistic;
